import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Pencil, MapPin, Phone, Mail, User, FileText } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";
import { StatusBadge, PriorityBadge, ElapsedTime } from "../components/badges";
import { ClientFormFields } from "./Clients";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [orders, setOrders] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [c, o] = await Promise.all([
        api.get(`/clients/${id}`),
        api.get(`/clients/${id}/orders`),
      ]);
      setClient(c.data);
      setOrders(o.data);
    } catch (e) {
      toast.error(apiError(e, "No se pudo cargar el cliente"));
      navigate("/clients");
    }
  }, [id, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const openEdit = () => {
    setForm({
      name: client.name || "", tax_id: client.tax_id || "",
      contact_person: client.contact_person || "", phone: client.phone || "",
      email: client.email || "", location: client.location || "", address: client.address || "",
    });
    setEditOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/clients/${id}`, form);
      toast.success("Cliente actualizado");
      setEditOpen(false);
      load();
    } catch (err) {
      toast.error(apiError(err, "No se pudo actualizar el cliente"));
    } finally {
      setSaving(false);
    }
  };

  if (!client || !orders) {
    return (
      <div className="space-y-3" data-testid="client-detail-loading">
        <Skeleton className="h-10 w-72" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  const info = [
    { icon: FileText, label: "RUC / Cédula", value: client.tax_id },
    { icon: User, label: "Persona de contacto", value: client.contact_person },
    { icon: Phone, label: "Teléfono", value: client.phone },
    { icon: Mail, label: "Correo electrónico", value: client.email },
    { icon: MapPin, label: "Ubicación", value: client.location },
  ];

  return (
    <div className="space-y-5" data-testid="client-detail">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/clients")} data-testid="client-back-button">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h2 className="font-heading text-2xl font-black tracking-tight text-slate-900" data-testid="client-name">{client.name}</h2>
            {client.address && <p className="text-sm text-slate-500">{client.address}</p>}
          </div>
        </div>
        <Button variant="outline" className="gap-1.5" onClick={openEdit} data-testid="edit-client-button">
          <Pencil className="h-4 w-4" /> Editar
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-4 rounded-lg border border-slate-200 bg-white p-5 sm:grid-cols-3 lg:grid-cols-5">
        {info.map((item) => (
          <div key={item.label}>
            <p className="flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] text-slate-500">
              <item.icon className="h-3.5 w-3.5" /> {item.label}
            </p>
            <p className="mt-1 text-sm font-medium text-slate-900" data-testid={`client-info-${item.label}`}>{item.value || "-"}</p>
          </div>
        ))}
      </div>

      <div className="rounded-lg border border-slate-200 bg-white">
        <div className="border-b border-slate-200 px-5 py-3">
          <h3 className="font-heading text-base font-bold text-slate-900">
            Órdenes asociadas <span className="ml-1 font-mono text-sm text-slate-400">({orders.length})</span>
          </h3>
        </div>
        {orders.length === 0 ? (
          <p className="p-5 text-sm text-slate-400" data-testid="client-orders-empty">Este cliente aún no tiene órdenes de servicio.</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-xs uppercase tracking-wider">N° Orden</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Técnico</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Prioridad</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Estado</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Fecha</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Tiempo</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orders.map((o) => (
                  <TableRow
                    key={o.id}
                    className="cursor-pointer transition-colors duration-200 hover:bg-slate-50"
                    onClick={() => navigate(`/orders/${o.id}`)}
                    data-testid={`client-order-row-${o.order_number}`}
                  >
                    <TableCell className="font-mono text-sm font-semibold text-primary">{o.order_number}</TableCell>
                    <TableCell className="text-sm text-slate-600">{o.technician_name}</TableCell>
                    <TableCell><PriorityBadge priority={o.priority} /></TableCell>
                    <TableCell><StatusBadge status={o.status} /></TableCell>
                    <TableCell className="text-sm text-slate-500">{(o.date || "").slice(0, 10)}</TableCell>
                    <TableCell>
                      {o.status !== "completado" ? (
                        <ElapsedTime since={o.created_at} high={o.priority === "alta"} />
                      ) : (
                        <span className="text-xs text-slate-400">—</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading">Editar Cliente</DialogTitle>
          </DialogHeader>
          {form && (
            <form onSubmit={handleSave} className="space-y-5" data-testid="client-edit-form">
              <ClientFormFields form={form} set={setForm} prefix="client-edit" />
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setEditOpen(false)}>Cancelar</Button>
                <Button type="submit" disabled={saving} className="bg-primary hover:bg-[#0353E9]" data-testid="client-edit-submit">
                  {saving ? "Guardando..." : "Guardar cambios"}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
